// Admin Context - Shared state and API functions for admin components
import { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [backups, setBackups] = useState([]);
  const [pushNotifications, setPushNotifications] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [manualPaymentStats, setManualPaymentStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTenants(data);
      }
    } catch (error) {
      console.error('Failed to fetch tenants', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/plans`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans', error);
    }
  }, []);

  const fetchPlatformSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPlatformSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    }
  }, []);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/backup/list`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBackups(data);
      }
    } catch (error) {
      console.error('Failed to fetch backups', error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/admin/list`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPushNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  }, []);

  const fetchManualPayments = useCallback(async () => {
    try {
      const [pendingRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/manual-payments/pending`, { credentials: 'include' }),
        fetch(`${API_URL}/api/manual-payments/stats/summary`, { credentials: 'include' })
      ]);
      
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setManualPayments(data);
      }
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setManualPaymentStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch manual payments', error);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchAnalytics(),
      fetchTenants(),
      fetchUsers(),
      fetchPlans(),
      fetchPlatformSettings(),
      fetchBackups(),
      fetchNotifications(),
      fetchManualPayments()
    ]);
    setLoading(false);
  }, [fetchStats, fetchAnalytics, fetchTenants, fetchUsers, fetchPlans, fetchPlatformSettings, fetchBackups, fetchNotifications, fetchManualPayments]);

  const savePlatformSettings = useCallback(async (section, data) => {
    try {
      const response = await fetch(`${API_URL}/api/platform/settings/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Settings saved successfully');
        await fetchPlatformSettings();
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
        return false;
      }
    } catch (error) {
      toast.error('Failed to save settings');
      return false;
    }
  }, [fetchPlatformSettings]);

  const value = {
    loading,
    setLoading,
    stats,
    analytics,
    tenants,
    users,
    plans,
    platformSettings,
    backups,
    pushNotifications,
    manualPayments,
    manualPaymentStats,
    fetchStats,
    fetchAnalytics,
    fetchTenants,
    fetchUsers,
    fetchPlans,
    fetchPlatformSettings,
    fetchBackups,
    fetchNotifications,
    fetchManualPayments,
    fetchAll,
    savePlatformSettings,
    setTenants,
    setPlans,
    setPlatformSettings,
    setBackups,
    setPushNotifications,
    setManualPayments
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

export { API_URL };
